import { useCallback, useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { Button } from "../../components/ui/Button";
import { getStandardDisplayName, normalizeComplianceStandardCode } from "../../utils/complianceStandard";
import GuidedAssessmentWorkspace from "./GuidedAssessmentWorkspace";
import OperationalComplianceJourneyWorkspace, { type OperationalJourneyWorkspaceSummary } from "./OperationalComplianceJourneyWorkspace";
import ccStyles from "../../styles/pages/compliance-center/ComplianceCenterOverview.module.scss";
import listStyles from "../../styles/pages/governance/GovernanceListing.module.scss";
import { featureFlags } from "../../config/featureFlags";
import { getActiveOrgId } from "../../utils/orgUtils";
import { fetchEffectiveStandardAccess, type EffectiveStandardAccess } from "../../services/standardAccessApi";
import OperationalWorkflowButton from "../../components/compliance-workflow/OperationalWorkflowButton";

const CC_ASSESSMENTS_TAB_KEY = "ccAssessmentsWorkspaceTab";

type EnabledStandardsResponse = {
  enabledStandards: string[];
  availableStandards: Array<{ code: string; name: string }>;
};

export default function ComplianceCenterAssessmentsWorkspace() {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const [standard, setStandard] = useState(() => normalizeComplianceStandardCode(localStorage.getItem("ccSelectedStandard")));
  const [assessmentTab, setAssessmentTab] = useState<"quick" | "journey">(() => {
    const n = normalizeComplianceStandardCode(localStorage.getItem("ccSelectedStandard"));
    if (n !== "ALL") return "journey";
    const persisted = localStorage.getItem(CC_ASSESSMENTS_TAB_KEY);
    return persisted === "journey" ? "journey" : "quick";
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAllSupported, setShowAllSupported] = useState(false);
  const [registry, setRegistry] = useState<EnabledStandardsResponse>({ enabledStandards: [], availableStandards: [] });
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  const showAnnexNote = useMemo(() => standard === "ISO27001", [standard]);
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [effectiveAccess, setEffectiveAccess] = useState<EffectiveStandardAccess | null>(null);

  useEffect(() => {
    if (!featureFlags.STANDARD_ACCESS_POLICY_V1 || !token) return;
    const orgId = getActiveOrgId();
    if (!orgId) return;
    void fetchEffectiveStandardAccess(orgId)
      .then(setEffectiveAccess)
      .catch(() => setEffectiveAccess(null));
  }, [token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED}?includeAll=${showAllSupported ? "1" : "0"}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json().catch(() => ({}));
        const data = (json?.data || {}) as EnabledStandardsResponse;
        setRegistry({
          enabledStandards: Array.isArray(data.enabledStandards) ? data.enabledStandards : [],
          availableStandards: Array.isArray(data.availableStandards) ? data.availableStandards : [],
        });
        setSelectedStandards(Array.isArray(data.enabledStandards) ? data.enabledStandards : []);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [showAllSupported, token]);

  useEffect(() => {
    const normalized = normalizeComplianceStandardCode(standard);
    localStorage.setItem("ccSelectedStandard", normalized);
  }, [standard]);

  useEffect(() => {
    if (standard !== "ALL" && localStorage.getItem(CC_ASSESSMENTS_TAB_KEY) === "quick") {
      localStorage.removeItem(CC_ASSESSMENTS_TAB_KEY);
    }
  }, [standard]);

  useEffect(() => {
    if (standard === "ALL") {
      localStorage.setItem(CC_ASSESSMENTS_TAB_KEY, assessmentTab);
    }
  }, [assessmentTab, standard]);

  useEffect(() => {
    if (standard !== "ALL" && assessmentTab === "quick") {
      setAssessmentTab("journey");
    }
  }, [assessmentTab, standard]);

  useEffect(() => {
    const applyStd = (raw: unknown) => {
      const next = normalizeComplianceStandardCode(raw ?? localStorage.getItem("ccSelectedStandard"));
      setStandard(next);
      if (next !== "ALL") setAssessmentTab("journey");
    };
    applyStd(localStorage.getItem("ccSelectedStandard"));
    const onSelectedStandard = (event: Event) => {
      const custom = event as CustomEvent<string>;
      applyStd(custom.detail);
    };
    const onStorage = () => applyStd(localStorage.getItem("ccSelectedStandard"));
    window.addEventListener("cc:selected-standard", onSelectedStandard as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("cc:selected-standard", onSelectedStandard as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const saveEnabledStandards = async () => {
    setSaving(true);
    try {
      await fetch(API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ standards: selectedStandards }),
      });
      setRegistry((prev) => ({ ...prev, enabledStandards: selectedStandards }));
    } finally {
      setSaving(false);
    }
  };

  const standardLabelForWorkspace = standard === "ALL" ? getStandardDisplayName(t, "ALL") : getStandardDisplayName(t, standard);

  const [assessmentNavMode, setAssessmentNavMode] = useState<"SEQUENTIAL" | "FREE">("SEQUENTIAL");
  const [journeySummary, setJourneySummary] = useState<OperationalJourneyWorkspaceSummary | null>(null);

  const onJourneySummaryChange = useCallback((next: OperationalJourneyWorkspaceSummary | null) => {
    setJourneySummary(next);
  }, []);

  useEffect(() => {
    if (standard === "ALL" && assessmentTab === "quick") setJourneySummary(null);
  }, [standard, assessmentTab]);

  type AssessmentTopicId =
    | "progress"
    | "full"
    | "documents"
    | "processes"
    | "roles"
    | "controls"
    | "score"
    | "recommendations"
    | "evidence";

  type AssessmentTopicDef = { id: AssessmentTopicId; title: string; scrollId: string };

  const assessmentTopicsOrdered = useMemo((): AssessmentTopicDef[] => {
    const topics: AssessmentTopicDef[] = [
      {
        id: "progress",
        title: t("complianceCenterUi.assessments.gridProgress", "Assessment progress"),
        scrollId: "cc-journey-questions-panel",
      },
      { id: "full", title: t("complianceCenterUi.assessments.gridFull", "Full Assessment"), scrollId: "cc-journey-recompute-strip" },
      {
        id: "documents",
        title: t("complianceCenterUi.assessments.gridDocuments", "Documents"),
        scrollId: "cc-journey-session-cards",
      },
      {
        id: "processes",
        title: t("complianceCenterUi.assessments.gridProcesses", "Processes"),
        scrollId: "cc-journey-questions-panel",
      },
      { id: "roles", title: t("complianceCenterUi.assessments.gridRoles", "Roles"), scrollId: "cc-journey-session-cards" },
    ];
    // Controls / Annex A topic only for ISO 27001
    if (showAnnexNote) {
      topics.push({
        id: "controls",
        title: t("complianceCenterUi.assessments.gridControlsIso", "Controls / Annex A (ISO integrated)"),
        scrollId: "cc-journey-questions-panel",
      });
    }
    topics.push(
      {
        id: "score",
        title: t("complianceCenterUi.assessments.gridScoreExplanation", "Score explanation"),
        scrollId: "cc-journey-blockers-explainability",
      },
      {
        id: "recommendations",
        title: t("complianceCenterUi.assessments.gridRecommendations", "Recommendations from failed/partial answers"),
        scrollId: "cc-journey-recommendations-api",
      },
      {
        id: "evidence",
        title: t("complianceCenterUi.assessments.gridEvidence", "Evidence requirements from failed/partial answers"),
        scrollId: "cc-journey-evidence-guidance",
      },
    );
    return topics;
  }, [showAnnexNote, t]);

  const assessmentTopics = useMemo(() => {
    if (assessmentNavMode === "FREE") return [...assessmentTopicsOrdered].sort((a, b) => a.title.localeCompare(b.title));
    return assessmentTopicsOrdered;
  }, [assessmentNavMode, assessmentTopicsOrdered]);

  const topicSubtitle = useCallback(
    (topicId: AssessmentTopicId, s: OperationalJourneyWorkspaceSummary | null) => {
      const fallback = t("complianceCenterUi.assessments.gridPlaceholder", "Use Overview and guided workspace for live status.");
      if (!s?.sessionId) return fallback;
      switch (topicId) {
        case "progress":
          return t(
            "complianceCenterUi.assessments.topicSubtitleProgress",
            "Session: {{answered}} / {{total}} questions answered · {{unanswered}} open ({{std}}).",
            { answered: s.answered, total: s.totalQ, unanswered: s.unanswered, std: s.journeyStandard },
          );
        case "full":
          return t(
            "complianceCenterUi.assessments.topicSubtitleFull",
            "Projections: compliant {{ok}}, partial {{partial}}, non-compliant {{bad}}, pending evidence {{pend}}.",
            {
              ok: s.compliantProjections,
              partial: s.partialProjections,
              bad: s.nonCompliantProjections,
              pend: s.pendingEvidenceProjections,
            },
          );
        case "documents":
          return t(
            "complianceCenterUi.assessments.topicSubtitleDocuments",
            "Session: {{linked}} YES/PARTIAL answers with linked evidence · {{gaps}} YES/PARTIAL without evidence IDs.",
            { linked: s.evidenceLinkedAnswers, gaps: s.evidenceMissingAnswers },
          );
        case "processes":
          return t(
            "complianceCenterUi.assessments.topicSubtitleProcesses",
            "Current domain: {{answered}} / {{total}} answered · {{missing}} without an answer.",
            { answered: s.domainAnswered, total: s.domainTotal, missing: s.domainMissingCount },
          );
        case "roles":
          return t(
            "complianceCenterUi.assessments.topicSubtitleRoles",
            "Use session setup to assign owners; {{open}} questions still open in this domain.",
            { open: s.domainMissingCount },
          );
        case "controls":
        case "score":
          return t(
            "complianceCenterUi.assessments.topicSubtitleControls",
            "Canonical projections (org): {{ok}} compliant · {{partial}} partial / pending evidence · {{bad}} non-compliant.",
            {
              ok: s.compliantProjections,
              partial: s.partialProjections + s.pendingEvidenceProjections,
              bad: s.nonCompliantProjections,
            },
          );
        case "recommendations":
          if (s.recommendationMatchMode === "unmapped_bulk" && s.recommendationsApiTotal > 0) {
            return t(
              "complianceCenterUi.assessments.topicSubtitleRecommendationsUnmapped",
              "{{count}} recommendations are available, but the API did not include control identifiers for precise matching.",
              { count: s.recommendationsApiTotal },
            );
          }
          if (s.recommendationMatchMode === "partial_identifiers" && s.recommendationsApiTotal > 0) {
            return t(
              "complianceCenterUi.assessments.topicSubtitleRecommendationsPartial",
              "{{matched}} of {{total}} recommendations matched at-risk controls; some rows were missing identifiers.",
              { matched: s.recommendationsForRiskControls, total: s.recommendationsApiTotal },
            );
          }
          return s.recommendationsForRiskControls > 0
            ? t(
                "complianceCenterUi.assessments.topicSubtitleRecommendationsHot",
                "{{count}} API recommendations target controls in PARTIAL / NON_COMPLIANT / PENDING_EVIDENCE — open Questions panel.",
                { count: s.recommendationsForRiskControls },
              )
            : t(
                "complianceCenterUi.assessments.topicSubtitleRecommendationsClear",
                "No recommendations mapped to at-risk controls in the current projection set.",
              );
        case "evidence":
          return s.evidenceMissingAnswers > 0
            ? t(
                "complianceCenterUi.assessments.topicSubtitleEvidenceGaps",
                "{{count}} saved YES/PARTIAL answers have no linked evidence IDs — tighten in Operational Journey.",
                { count: s.evidenceMissingAnswers },
              )
            : t(
                "complianceCenterUi.assessments.topicSubtitleEvidenceOk",
                "No YES/PARTIAL answers without linked evidence in this session snapshot.",
              );
        default:
          return fallback;
      }
    },
    [t],
  );

  const topicCardToneClass = (topicId: AssessmentTopicId, s: OperationalJourneyWorkspaceSummary | null): string => {
    if (!s?.sessionId) return "";
    if (topicId === "recommendations") {
      if ((s.recommendationMatchMode === "unmapped_bulk" || s.recommendationMatchMode === "partial_identifiers") && s.recommendationsApiTotal > 0) {
        return ccStyles.metricCardTopicAmber;
      }
      if (s.recommendationsForRiskControls > 0) return ccStyles.metricCardTopicRed;
      return ccStyles.metricCardTopicGreen;
    }
    if (topicId === "evidence") {
      if (s.evidenceMissingAnswers > 0) return ccStyles.metricCardTopicAmber;
      return ccStyles.metricCardTopicGreen;
    }
    if (topicId === "controls" || topicId === "score" || topicId === "full") {
      if (s.nonCompliantProjections > 0) return ccStyles.metricCardTopicRed;
      if (s.partialProjections + s.pendingEvidenceProjections > 0) return ccStyles.metricCardTopicAmber;
      if (s.compliantProjections > 0) return ccStyles.metricCardTopicGreen;
    }
    if (topicId === "progress") {
      if (s.unanswered === 0 && s.totalQ > 0) return ccStyles.metricCardTopicGreen;
      if (s.unanswered > 0) return ccStyles.metricCardTopicAmber;
    }
    return "";
  };

  const emitJourneyCommand = useCallback((detail: { type: string; elementId?: string }) => {
    window.dispatchEvent(new CustomEvent("cc:journey-command", { detail }));
  }, []);

  const topicPrimaryAction = useCallback(
    (topicId: AssessmentTopicId) => {
      const row = assessmentTopicsOrdered.find((x) => x.id === topicId);
      const scrollToId = (id: string) => emitJourneyCommand({ type: "scroll_element", elementId: id });
      switch (topicId) {
        case "progress":
          if (!journeySummary?.sessionId) emitJourneyCommand({ type: "open_session_setup" });
          else emitJourneyCommand({ type: "focus_questions_panel" });
          return;
        case "full":
          if (journeySummary?.sessionId) emitJourneyCommand({ type: "run_recompute" });
          else if (row?.scrollId) scrollToId(row.scrollId);
          return;
        case "documents":
          scrollToId("cc-journey-session-cards");
          return;
        case "processes":
          emitJourneyCommand({ type: "open_domains_modal" });
          return;
        case "roles":
          emitJourneyCommand({ type: "open_session_setup" });
          return;
        case "controls":
          emitJourneyCommand({ type: "focus_questions_panel" });
          return;
        case "score":
          scrollToId("cc-journey-blockers-explainability");
          return;
        case "recommendations":
          scrollToId("cc-journey-recommendations-api");
          return;
        case "evidence":
          scrollToId("cc-journey-evidence-guidance");
          return;
        default:
          if (row?.scrollId) scrollToId(row.scrollId);
      }
    },
    [assessmentTopicsOrdered, emitJourneyCommand, journeySummary?.sessionId],
  );

  const topicAriaLabel = useCallback(
    (topicId: AssessmentTopicId) => {
      const def = assessmentTopicsOrdered.find((x) => x.id === topicId);
      const title = def?.title || "";
      switch (topicId) {
        case "progress":
          return !journeySummary?.sessionId
            ? t("complianceCenterUi.assessments.topicActionProgressNoSession", "Open session setup ({{title}})", { title })
            : t("complianceCenterUi.assessments.topicActionProgress", "Go to current question ({{title}})", { title });
        case "full":
          return journeySummary?.sessionId
            ? t("complianceCenterUi.assessments.topicActionFullRecompute", "Recompute readiness projections ({{title}})", { title })
            : t("complianceCenterUi.assessments.topicActionFullScroll", "Open readiness workbench ({{title}})", { title });
        case "documents":
          return t("complianceCenterUi.assessments.topicActionDocuments", "Scroll to session context ({{title}})", { title });
        case "processes":
          return t("complianceCenterUi.assessments.topicActionProcesses", "Browse domains ({{title}})", { title });
        case "roles":
          return t("complianceCenterUi.assessments.topicActionRoles", "Open session setup for owners ({{title}})", { title });
        case "controls":
          return t("complianceCenterUi.assessments.topicActionControls", "Focus questions panel ({{title}})", { title });
        case "score":
          return t("complianceCenterUi.assessments.topicActionScore", "Open explainability and status ({{title}})", { title });
        case "recommendations":
          return t("complianceCenterUi.assessments.topicActionRecommendations", "Scroll to API recommendations ({{title}})", { title });
        case "evidence":
          return t("complianceCenterUi.assessments.topicActionEvidence", "Scroll to evidence guidance ({{title}})", { title });
        default:
          return title;
      }
    },
    [assessmentTopicsOrdered, journeySummary?.sessionId, t],
  );

  const journeyGreenPrimary = "!bg-green-600 hover:!bg-green-700 !border-green-600";

  const showAssessmentNavControls = !loading && registry.enabledStandards.length > 0;

  return (
    <div className={ccStyles.root}>
      <section className={listStyles.panel}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("complianceCenterUi.journey.activeStandardReadOnly", "Active standard")}
            </p>
            <h2 className="text-lg font-semibold text-slate-900">{standardLabelForWorkspace}</h2>
          </div>
          {standard !== "ALL" ? (
            <OperationalWorkflowButton standardCode={standard} effectiveAccess={effectiveAccess} />
          ) : null}
        </div>
        {isAdmin ? (
          <details className="mt-4 border-t border-slate-100 pt-3">
            <summary className="cursor-pointer text-sm font-semibold text-slate-800">
              {t("complianceCenterUi.standards.adminEnablement", "Admin: enabled standards")}
            </summary>
          <div className="mt-3 space-y-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={showAllSupported} onChange={(event) => setShowAllSupported(event.target.checked)} />
              {t("complianceCenterUi.standards.showAll", "Show all supported standards")}
            </label>
            <div className="flex flex-wrap gap-2">
              {registry.availableStandards.map((item) => {
                const checked = selectedStandards.includes(item.code);
                return (
                  <label key={item.code} className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => {
                        setSelectedStandards((prev) =>
                          event.target.checked ? [...prev, item.code] : prev.filter((code) => code !== item.code),
                        );
                      }}
                    />
                    {item.name || item.code}
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              className="rounded-md bg-sidebarActive px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={() => void saveEnabledStandards()}
              disabled={saving}
            >
              {saving ? t("complianceCenterUi.standards.saving", "Saving...") : t("complianceCenterUi.standards.save", "Save enabled standards")}
            </button>
          </div>
          </details>
        ) : null}
      </section>

      {registry.enabledStandards.length === 0 ? (
        <div className={`${ccStyles.metricCard} border-amber-200 bg-amber-50/90 text-amber-900`} role="status">
          <p className="text-sm">
            {t(
              "complianceCenterUi.assessments.noEnabledStandards",
              "No enabled standards found. Complete Starter Guide or ask an admin to enable standards first.",
            )}
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {standard === "ALL" ? (
          <>
            <button
              type="button"
              data-testid="quick-self-assessment-tab"
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                assessmentTab === "quick" ? "bg-sidebarActive text-white" : "border border-gray-200 bg-white text-gray-700"
              }`}
              onClick={() => setAssessmentTab("quick")}
            >
              {t("complianceCenterUi.assessments.tabQuick", "Quick Self-Assessment")}
            </button>
            <button
              type="button"
              data-testid="operational-journey-tab"
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                assessmentTab === "journey" ? "bg-sidebarActive text-white" : "border border-gray-200 bg-white text-gray-700"
              }`}
              onClick={() => setAssessmentTab("journey")}
            >
              {t("complianceCenterUi.assessments.tabJourney", "Operational Journey")}
            </button>
          </>
        ) : null}
      </div>

      {showAssessmentNavControls ? (
        <section className={listStyles.panel}>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("complianceCenterUi.journey.modeLabel", "Navigation mode")}
          </div>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="small"
              color={assessmentNavMode === "SEQUENTIAL" ? "primary" : "outline"}
              className={`flex-1 ${assessmentNavMode === "SEQUENTIAL" ? journeyGreenPrimary : ""}`}
              onClick={() => setAssessmentNavMode("SEQUENTIAL")}
            >
              {t("complianceCenterUi.journey.modeSequential", "Sequential")}
            </Button>
            <Button
              type="button"
              size="small"
              color={assessmentNavMode === "FREE" ? "primary" : "outline"}
              className={`flex-1 ${assessmentNavMode === "FREE" ? journeyGreenPrimary : ""}`}
              onClick={() => setAssessmentNavMode("FREE")}
            >
              {t("complianceCenterUi.journey.modeFree", "Free navigation")}
            </Button>
          </div>
          {assessmentNavMode === "FREE" ? (
            <p className="mt-2 text-xs text-slate-600">
              {t(
                "complianceCenterUi.assessments.freeNavTopicsHint",
                "Topic cards below follow free order. Open the Operational Journey to assign owners or hand work to other teams in any sequence.",
              )}
            </p>
          ) : null}
        </section>
      ) : null}

      {loading ? (
        <div className={`${ccStyles.metricSubtitle} text-slate-600`}>{t("common.loading", "Loading...")}</div>
      ) : standard === "ALL" && assessmentTab === "quick" ? (
        <GuidedAssessmentWorkspace standardCode={standard} enabledStandards={registry.enabledStandards} />
      ) : null}

      {!(loading || (standard === "ALL" && assessmentTab === "quick")) ? (
        <OperationalComplianceJourneyWorkspace
          enabledStandards={registry.enabledStandards}
          preferredStandard={standard !== "ALL" ? standard : undefined}
          navigationMode={assessmentNavMode}
          onNavigationModeChange={setAssessmentNavMode}
          onWorkspaceSummaryChange={onJourneySummaryChange}
        />
      ) : null}

    </div>
  );
}
